// #1337 (D-CAT-10) — <CanvasMultiSelectField /> — a multi-valued vocabulary field in the
// SAME provenance envelope CanvasSelectField uses (FieldTintWrapper + FormField +
// ProvenanceChip + ConflictChooser + FieldNotices), so an AI-filled boat_types set shows
// its chip and toggling a type flips it to "✓ Edited by you" via markEdited().

import { useFormContext } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@marine/ui/form';
import type { BoatOption } from '@/modules/inventory/constants/boat-options';
import type { ProvenanceEntry } from '@/modules/inventory/types/boat.types';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import { VocabularyMultiSelect } from '@/components/combobox/VocabularyMultiSelect';
import { FieldTintWrapper } from '../FieldTintWrapper';
import { ProvenanceChip } from '../ProvenanceChip';
import { ConflictChooser } from '../ConflictChooser';
import { FieldNotices } from '../CanvasReview';
import { useProvenance } from '../hooks/useProvenance';
import { sortByAuthority } from '../lib/sourceAuthority';

type WizardFieldName = keyof WizardFormData;

interface CanvasMultiSelectFieldProps {
  /** A top-level WizardFormData key holding string[] (boat_types). */
  fieldName: WizardFieldName;
  label: string;
  options: readonly BoatOption[];
  placeholder?: string;
  loading?: boolean;
}

export function CanvasMultiSelectField({ fieldName, label, options, placeholder, loading = false }: CanvasMultiSelectFieldProps) {
  const { control } = useFormContext<WizardFormData>();
  const { state, clear, selectSource, markEdited } = useProvenance(fieldName);

  return (
    <FieldTintWrapper fieldName={fieldName}>
      <FormField
        control={control}
        name={fieldName as never}
        render={({ field }) => {
          const current = Array.isArray(field.value) ? (field.value as string[]) : [];

          return (
            <FormItem>
              <div className="flex items-center justify-between gap-2">
                <FormLabel className="text-xs">{label}</FormLabel>
                <ProvenanceChip fieldName={fieldName} entry={state.entry} onClear={clear} isLowConfidence={state.isLowConfidence} />
              </div>
              {state.isConflict && Array.isArray(state.entry) && (
                <ConflictChooser
                  fieldName={fieldName}
                  variant="radio"
                  entries={sortByAuthority(state.entry as ProvenanceEntry[])}
                  selectedIndex={state.pickedIndex}
                  onSelectOne={(entry) => selectSource(entry)}
                />
              )}
              <FieldNotices fieldName={fieldName} />
              <FormControl>
                <VocabularyMultiSelect
                  value={current}
                  onChange={(next) => {
                    field.onChange(next);
                    markEdited();
                  }}
                  options={options}
                  loading={loading}
                  placeholder={placeholder}
                  aria-label={label}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </FieldTintWrapper>
  );
}
